import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '../aura';
import { getSitekey } from '../features/turnstile-constants';
import { useFormContext } from '../state';
import {
  Email,
  FirstName,
  LastName,
  Marketing,
  Password,
  PhoneNumber,
  Privacy,
} from './input';
import { Form, Prompt, Title } from './templates';

const TURNSTILE_SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js';

/**
 * Loads the Cloudflare Turnstile widget while the user fills in their details.
 * In most cases Cloudflare resolves the challenge passively and the widget
 * stays invisible. If a challenge is required the checkbox appears at the
 * bottom of the form so the user can tick it before clicking Sign Up.
 *
 * The collected token is stored in FormContextData. After a successful job,
 * step-machine attempts authentication immediately with that token — no extra
 * screen or click required for most users.
 *
 * The fallback to STEP_TURNSTILE occurs when the token is absent (unsupported
 * domain) or has expired before authentication runs. Token expiry is more
 * likely for users who go through email verification, since the total elapsed
 * time from token issuance to authentication can exceed the token TTL.
 */
export function StepFourForm() {
  const { t } = useTranslation('stepFourForm');
  const { setData } = useFormContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const siteKey = getSitekey(window.location.hostname) ?? '';

  const setToken = useCallback(
    (token: string) => {
      setData(prev => ({ ...prev, turnstileToken: token }));
    },
    [setData],
  );

  const clearToken = useCallback(() => {
    setData(prev => ({ ...prev, turnstileToken: undefined }));
  }, [setData]);

  // Stable refs so widget callbacks never cause the render effect to re-run.
  const setTokenRef = useRef(setToken);
  setTokenRef.current = setToken;
  const clearTokenRef = useRef(clearToken);
  clearTokenRef.current = clearToken;

  const onToken = useCallback((token: string) => {
    setTokenRef.current(token);
  }, []);

  const onExpire = useCallback(() => {
    clearTokenRef.current();
  }, []);

  const onError = useCallback(() => {
    clearTokenRef.current();
  }, []);

  useEffect(() => {
    if (!siteKey) return;

    const renderWidget = () => {
      if (!containerRef.current || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        callback: onToken,
        'expired-callback': onExpire,
        'error-callback': onError,
        // 'interaction-only' hides the widget entirely when Cloudflare can
        // resolve the challenge passively, and only shows the checkbox when
        // the user must interact. This means the container stays invisible
        // for most users and expands naturally only when needed — no clipping.
        appearance: 'interaction-only',
      });
    };

    let scriptEl: HTMLScriptElement | null = null;

    if (window.turnstile) {
      renderWidget();
    } else {
      const existing = document.querySelector<HTMLScriptElement>(
        `script[src="${TURNSTILE_SCRIPT_SRC}"]`,
      );
      scriptEl = existing ?? document.createElement('script');
      if (!existing) {
        scriptEl.src = TURNSTILE_SCRIPT_SRC;
        scriptEl.async = true;
        // Surface real error details to Datadog instead of an opaque
        // cross-origin "Script error." (Cloudflare sends Access-Control-Allow-Origin).
        scriptEl.crossOrigin = 'anonymous';
        document.head.appendChild(scriptEl);
      }
      scriptEl.addEventListener('load', renderWidget);
    }

    return () => {
      scriptEl?.removeEventListener('load', renderWidget);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
      // Clear the token on unmount so a stale value never survives navigation.
      clearTokenRef.current();
    };
  }, [siteKey, onToken, onExpire, onError]);

  return (
    <Form>
      <Title text={t('title')} showStepCounter />
      <Prompt text={t('prompt1')} />
      <Grid>
        <div className="col-sm-12 col-md-6">
          <FirstName />
        </div>
        <div className="col-sm-12 col-md-6">
          <LastName />
        </div>
        <div className="col-sm-12">
          <Email />
        </div>
        <div className="col-sm-12">
          <PhoneNumber />
        </div>
        <div className="col-sm-12">
          <Password />
        </div>
        <div className="col-sm-12">
          <Privacy />
        </div>
        <div className="col-sm-12">
          <Marketing />
        </div>
      </Grid>
      {/*
       * No explicit dimensions — 'interaction-only' keeps the widget invisible
       * unless Cloudflare requires a challenge, in which case it expands to its
       * natural size so the user can tick the checkbox without obstruction.
       */}
      <div ref={containerRef} />
    </Form>
  );
}
